import React from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import { ArrowLeft, Save, Search, FileText, User, Car, Loader2, Truck } from 'lucide-react';
import { DateInput } from '@/components/Service/DateInput';
import { NotifyCustomerModal } from '@/components/Service/NotifyCustomerModal';
import { useServiceMasters } from '@/hooks/useServiceMasters';
import { calcJobHours, calcDaysDelay } from '@/components/Service/jobMetrics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Helpers --------------------------------------------------------------
// Job Hours / Days Delay calculators live in `@/components/Service/jobMetrics`
// so the same logic is reused across this form, the All-Jobs list, and reports.

// --- Reusable form atoms --------------------------------------------------
const Field = ({ label, required, children, hint, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-[12px] font-medium text-slate-600">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const inputCx = 'w-full px-3 py-2.5 rounded-xl bg-white/80 border border-slate-200 text-[14px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all';

const TextInput = ({ value, onChange, ...rest }) => (
  <input value={value || ''} onChange={onChange} className={inputCx} {...rest} />
);

const Select = ({ value, onChange, options, placeholder = 'Select…', testId }) => {
  // Ensure the current value is always present as an option even if it's not in the master list
  // (e.g., values uploaded from Excel that haven't been added to masters yet)
  const optionNames = new Set(options.map((o) => (o.name || o)));
  const showOrphan = value && !optionNames.has(value);
  return (
    <select value={value || ''} onChange={onChange} data-testid={testId} className={inputCx}>
      <option value="">{placeholder}</option>
      {showOrphan && <option value={value}>{value} (not in masters)</option>}
      {options.map((o) => (
        <option key={o.id || o} value={o.name || o}>{o.name || o}</option>
      ))}
    </select>
  );
};

// --- Page -----------------------------------------------------------------
export default function ServiceJobForm({ user, onLogout, mode: initialMode = 'create' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const mode = initialMode || (id ? 'edit' : 'create');
  const readOnly = mode === 'view';
  const isEdit = mode === 'edit' || mode === 'view';

  // Service Technician role: restricted to editing only Status + Ramp In/Out
  const isTechnician = user?.role_name === 'Service Technician';

  // Where to return after save / back — comes from "from" query param or browser history
  const fromParam = new URLSearchParams(location.search).get('from');
  const returnTo = fromParam === 'jobs' ? '/service/jobs' : '/service/jobs';

  const [tab, setTab] = React.useState('job');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(isEdit);
  const [fetchingChassis, setFetchingChassis] = React.useState(false);

  // Track original status (loaded from DB) so we can detect a status change on save
  const [originalStatus, setOriginalStatus] = React.useState('');

  // Notify customer modal — shown after save when status changes
  const [notifyState, setNotifyState] = React.useState({ open: false, jobId: null, status: null, customer: null });

  // Service masters — auto-refreshes when ServiceMasters.js mutates a row
  // (broadcasts `masters:changed` event). This keeps every dropdown live.
  const masters = useServiceMasters();

  // Service tenant config — drives whether Job-No auto-fills or is manual.
  const [serviceConfig, setServiceConfig] = React.useState(null);
  React.useEffect(() => {
    if (mode !== 'create') return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const { data } = await axios.get(`${API}/service/config`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setServiceConfig(data);
      } catch {
        // Non-fatal — fall back to auto behaviour
        if (!cancelled) setServiceConfig({ job_no_auto_generate: true });
      }
    })();
    return () => { cancelled = true; };
  }, [mode]);
  const manualJobNo = mode === 'create' && serviceConfig && serviceConfig.job_no_auto_generate === false;

  // iter-114 — Quick Service Job mode (?quick=1 on /service/jobs/new).
  // Quick jobs ALWAYS auto-number with a "QS{n}" prefix on the backend, so
  // the form hides the Job-No input entirely (no manual override possible).
  // For edit/view modes we mirror the underlying job's `is_quick` flag once
  // it loads, but the quick-mode banner only shows during create.
  const [searchParams] = useSearchParams();
  const isQuickCreate = mode === 'create' && searchParams.get('quick') === '1';
  const [editingIsQuick, setEditingIsQuick] = React.useState(false);
  const isQuickContext = isQuickCreate || editingIsQuick;

  const [form, setForm] = React.useState({
    job_no: '',
    doc_type: '',
    job_date: '',
    service_head: '',
    job_category: '',
    job_type: '',
    supervisor: '',
    technician: '',
    customer_voice: '',
    customer_type: '',
    vehicle_in_date: '',
    vehicle_in_time: '',
    expected_delivery_date: '',
    expected_delivery_time: '',
    ramp_in_date: '',
    ramp_in_time: '',
    ramp_out_date: '',
    ramp_out_time: '',
    party_name: '',
    mobile_no_1: '',
    mobile_no_2: '',
    booking_no: '',
    email: '',
    brand_name: '',
    model: '',
    variant: '',
    color: '',
    chassis_no: '',
    engine_no: '',
    battery_no: '',
    charger_no: '',
    controller_no: '',
    reg_no: '',
    kms: '',
    // iter-121: Additional Customer Details (Address / Insurance / DMS)
    // Auto-fetched from Sales Module (chassis → reg_no → mobile fallback) or
    // entered manually. Stored on the service_job document only — Sales bookings
    // are read-only from the Service Module's perspective.
    building_name: '',
    door_no: '',
    address_1: '',
    address_2: '',
    address_3: '',
    area: '',
    city: '',
    state: '',
    pin_code: '',
    insurance_company_name: '',
    policy_no: '',
    own_damage_expiry_date: '',
    dms_invoice_no: '',
    dms_invoice_date: '',
  });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // Auto-fill prompt: when user transitions Status → "Delivered" in EDIT mode,
  // ask for confirmation, then auto-fill Expected Delivery Date (today) + Time (now).
  // The user can decline and still save the new status manually; the date/time are
  // freely editable afterwards.
  const [deliveredPrompt, setDeliveredPrompt] = React.useState({ open: false, newStatus: '' });

  const isDeliveredStatus = (s) => String(s || '').trim().toLowerCase() === 'delivered';

  const handleStatusChange = (e) => {
    const next = e?.target ? e.target.value : e;
    const wasDelivered = isDeliveredStatus(form.doc_type);
    const willBeDelivered = isDeliveredStatus(next);
    setForm((f) => ({ ...f, doc_type: next }));
    // Only fire on edit mode, only on the transition (not when already Delivered)
    if (mode === 'edit' && willBeDelivered && !wasDelivered) {
      setDeliveredPrompt({ open: true, newStatus: next });
    }
  };

  const todayDDMMYYYY = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
  };
  const nowHHmmDot = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}.${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const confirmDeliveredAutofill = () => {
    setForm((f) => ({
      ...f,
      expected_delivery_date: todayDDMMYYYY(),
      expected_delivery_time: nowHHmmDot(),
    }));
    setDeliveredPrompt({ open: false, newStatus: '' });
  };
  const cancelDeliveredAutofill = () => setDeliveredPrompt({ open: false, newStatus: '' });

  // Masters are now provided by the live `useServiceMasters` hook above.

  // Load existing job in edit/view
  React.useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/service/jobs/${id}`);
        if (cancelled) return;
        setForm((f) => ({ ...f, ...data }));
        setOriginalStatus(data.doc_type || '');
        setEditingIsQuick(!!data.is_quick);
      } catch {
        toast.error('Failed to load job');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  // iter-121: Combo lookup — chassis → reg_no → mobile_no_1 fallback chain.
  // Lets the user fetch from Sales using whichever key they have on hand.
  // Backend prioritises chassis, then reg_no, then mobile_no_1/_2.
  const fetchFromSales = async () => {
    const chassis = (form.chassis_no || '').trim();
    const reg = (form.reg_no || '').trim();
    const mob = (form.mobile_no_1 || '').trim();
    if (!chassis && !reg && !mob) {
      toast.error('Enter Chassis No, Registration No, or Mobile No to fetch');
      return;
    }
    setFetchingChassis(true);
    try {
      const params = new URLSearchParams();
      if (chassis) params.set('chassis', chassis);
      if (reg) params.set('reg_no', reg);
      if (mob) params.set('mobile', mob);
      const { data } = await axios.get(`${API}/service/lookup/customer?${params.toString()}`);
      setForm((f) => ({
        ...f,
        // Existing customer + vehicle fields
        party_name: data.party_name || f.party_name,
        mobile_no_1: data.mobile_no_1 || f.mobile_no_1,
        mobile_no_2: data.mobile_no_2 || f.mobile_no_2,
        booking_no: data.booking_no || f.booking_no,
        email: data.email || f.email,
        brand_name: data.brand || f.brand_name,
        model: data.model || f.model,
        variant: data.variant || f.variant,
        color: data.color || f.color,
        engine_no: data.engine_no || f.engine_no,
        battery_no: data.battery_no || f.battery_no,
        charger_no: data.charger_no || f.charger_no,
        controller_no: data.controller_no || f.controller_no,
        reg_no: data.reg_no || f.reg_no,
        // iter-121: 14 additional Customer Details
        building_name: data.building_name || f.building_name,
        door_no: data.door_no || f.door_no,
        address_1: data.address_1 || f.address_1,
        address_2: data.address_2 || f.address_2,
        address_3: data.address_3 || f.address_3,
        area: data.area || f.area,
        city: data.city || f.city,
        state: data.state || f.state,
        pin_code: data.pin_code || f.pin_code,
        insurance_company_name: data.insurance_company_name || f.insurance_company_name,
        policy_no: data.policy_no || f.policy_no,
        own_damage_expiry_date: data.own_damage_expiry_date || f.own_damage_expiry_date,
        dms_invoice_no: data.dms_invoice_no || f.dms_invoice_no,
        dms_invoice_date: data.dms_invoice_date || f.dms_invoice_date,
      }));
      const matchedBy = data.matched_by ? ` (matched by ${data.matched_by})` : '';
      toast.success(`Customer details fetched from Sales${matchedBy}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No matching booking found');
    } finally {
      setFetchingChassis(false);
    }
  };

  const handleBack = () => {
    // Prefer browser history if available, else fallback to /service/jobs
    if (window.history.length > 1) navigate(-1);
    else navigate(returnTo);
  };

  const handleSave = async () => {
    if (!form.job_date) { toast.error('Job Date is required'); setTab('job'); return; }
    if (!form.party_name) { toast.error('Party Name is required'); setTab('customer'); return; }
    // Quick-mode skips the manual-job-no validation entirely (always auto-numbered).
    if (manualJobNo && !isQuickContext && !String(form.job_no || '').trim()) {
      toast.error('Job No is required (auto-generation is disabled)');
      setTab('job');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload._id;
      // In manual mode (and NOT a quick job), KEEP the user-entered job_no;
      // otherwise drop it so the server auto-assigns.
      if (manualJobNo && !isQuickContext) {
        payload.job_no = String(form.job_no || '').trim();
      } else {
        delete payload.job_no;
      }
      delete payload.job_no_int; delete payload.financial_year;
      delete payload.created_at; delete payload.updated_at;
      delete payload.sla_status; delete payload.days_late;
      // iter-114 — flag the new job as Quick when ?quick=1 is in the URL.
      // Edit mode keeps whatever flag the row already has on the server.
      if (mode === 'create') {
        payload.is_quick = isQuickCreate;
      } else {
        delete payload.is_quick;
      }

      let savedJobId = id;
      if (mode === 'create') {
        const { data } = await axios.post(`${API}/service/jobs`, payload);
        savedJobId = data.id;
        toast.success(`Job #${data.job_no} created`);
      } else {
        await axios.put(`${API}/service/jobs/${id}`, payload);
        toast.success('Job updated');
      }

      // If status changed (and we have a valid status), prompt to notify the customer.
      const statusChanged = (form.doc_type || '') && form.doc_type !== originalStatus;
      const hasContact = !!(form.mobile_no_1 || form.email);
      if (statusChanged && hasContact && savedJobId) {
        setNotifyState({
          open: true,
          jobId: savedJobId,
          status: form.doc_type,
          customer: { mobile_no_1: form.mobile_no_1, email: form.email },
        });
        return; // Hold navigation until the user dismisses the modal
      }

      handleBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'job', label: 'Job Details', icon: FileText },
    { id: 'customer', label: 'Customer Details', icon: User },
    { id: 'vehicle', label: 'Vehicle Details', icon: Car },
  ];

  const titleText = mode === 'create'
    ? (isQuickCreate ? 'New Quick Service Job' : 'New Job Card')
    : mode === 'edit'
      ? `Edit ${editingIsQuick ? 'Quick ' : ''}Job #${form.job_no || ''}`
      : `View ${editingIsQuick ? 'Quick ' : ''}Job #${form.job_no || ''}`;

  const headerActions = (
    <div className="flex items-center gap-2">
      <button
        onClick={handleBack}
        data-testid="job-form-cancel-btn"
        className="px-3.5 py-2 rounded-xl bg-white/70 border border-slate-200 text-slate-700 hover:bg-white transition-all text-[13px] font-medium flex items-center gap-2"
      >
        <ArrowLeft size={15} /> Back
      </button>
      {!readOnly && (
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="job-form-save-btn"
          className="px-4 py-2 rounded-xl text-white text-[13px] font-medium shadow-sm shadow-indigo-500/20 hover:shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 60%, #DB2777 100%)' }}
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {mode === 'create' ? 'Create Job' : 'Save Changes'}
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <ServiceLayout user={user} onLogout={onLogout} title="Loading…">
        <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 p-12 flex items-center justify-center">
          <Loader2 className="animate-spin text-indigo-500" size={28} />
        </div>
      </ServiceLayout>
    );
  }

  return (
    <ServiceLayout user={user} onLogout={onLogout} title={titleText} subtitle="Service job card" actions={headerActions}>
      <div className="rounded-2xl bg-white/70 backdrop-blur border border-white/50 shadow-[0_8px_30px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="job-form-card">
        <div className="flex items-center gap-1 px-2 pt-2 border-b border-slate-200/60 bg-white/40">
          {tabs.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`relative px-4 py-2.5 text-[13px] font-medium rounded-t-lg flex items-center gap-2 transition-all
                  ${active ? 'text-indigo-700 bg-white' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <Icon size={15} />
                {t.label}
                {active && <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-gradient-to-r from-indigo-500 to-fuchsia-500 rounded-full" />}
              </button>
            );
          })}
        </div>

        <fieldset disabled={readOnly} className={`p-5 md:p-7 ${readOnly ? 'opacity-95' : ''}`}>
          {tab === 'job' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field
                label={isQuickContext ? 'Quick Job No' : (manualJobNo ? 'Job No *' : 'Job No')}
                hint={isQuickContext
                  ? 'Auto-assigned with QS prefix on save (e.g. QS1, QS2)'
                  : manualJobNo
                    ? 'Manual entry required (auto-generation is disabled)'
                    : 'Auto-generated, sequential per Financial Year'}
              >
                {isQuickContext ? (
                  <input
                    value={form.job_no || (mode === 'create' ? 'QS Auto' : '')}
                    disabled
                    data-testid="quick-job-no-display"
                    className={`${inputCx} bg-emerald-50 border-emerald-200 text-emerald-700 font-mono font-semibold`}
                  />
                ) : manualJobNo ? (
                  <input
                    type="text"
                    value={form.job_no || ''}
                    onChange={setField('job_no')}
                    placeholder="Enter Job No"
                    className={inputCx}
                    data-testid="job-no-manual-input"
                  />
                ) : (
                  <input
                    value={form.job_no || (mode === 'create' ? 'Auto' : '')}
                    disabled
                    className={`${inputCx} bg-slate-50 text-slate-500`}
                  />
                )}
              </Field>
              <Field label="Status">
                <Select value={form.doc_type} onChange={handleStatusChange} options={masters.status} placeholder="Select status" testId="status-select" />
              </Field>
              <fieldset disabled={isTechnician} className="contents">
              <Field label="Job Date" required hint="DD-MM-YYYY">
                <DateInput value={form.job_date} onChange={(v) => setForm((f) => ({ ...f, job_date: v }))} testId="job-date" />
              </Field>

              <Field label="Service Head">
                <Select value={form.service_head} onChange={setField('service_head')} options={masters.service_head} placeholder="Select service head" testId="service-head-select" />
              </Field>
              <Field label="Job Category">
                <Select value={form.job_category} onChange={setField('job_category')} options={masters.job_category} placeholder="Select category" testId="job-category-select" />
              </Field>
              <Field label="Job Type">
                <Select value={form.job_type} onChange={setField('job_type')} options={masters.job_type} placeholder="Select type" testId="job-type-select" />
              </Field>

              <Field label="Supervisor">
                <Select value={form.supervisor} onChange={setField('supervisor')} options={masters.supervisor} placeholder="Select supervisor" testId="supervisor-select" />
              </Field>
              <Field label="Technician">
                <Select value={form.technician} onChange={setField('technician')} options={masters.technician} placeholder="Select technician" testId="technician-select" />
              </Field>
              <Field label="Customer Type">
                <Select value={form.customer_type} onChange={setField('customer_type')} options={masters.customer_type} placeholder="Select customer type" testId="customer-type-select" />
              </Field>

              <Field label="Customer's Voice" className="md:col-span-3">
                <textarea
                  value={form.customer_voice || ''}
                  onChange={setField('customer_voice')}
                  rows={3}
                  className={inputCx}
                  placeholder="Concerns reported by the customer…"
                  data-testid="customer-voice"
                />
              </Field>

              <Field label="Vehicle In Date" hint="DD-MM-YYYY">
                <DateInput value={form.vehicle_in_date} onChange={(v) => setForm((f) => ({ ...f, vehicle_in_date: v }))} testId="vehicle-in-date" />
              </Field>
              <Field label="Vehicle In Time" hint="HH:MM (24-hour) — Excel AM/PM auto-converts">
                <TextInput type="time" value={form.vehicle_in_time} onChange={setField('vehicle_in_time')} data-testid="vehicle-in-time" />
              </Field>
              <Field label="Expected Delivery Date" hint="DD-MM-YYYY">
                <DateInput value={form.expected_delivery_date} onChange={(v) => setForm((f) => ({ ...f, expected_delivery_date: v }))} testId="expected-delivery-date" />
              </Field>
              <Field label="Expected Delivery Time" hint="HH.MM (e.g. 14.30)">
                <TextInput
                  type="text"
                  inputMode="numeric"
                  placeholder="HH.MM"
                  value={form.expected_delivery_time}
                  onChange={setField('expected_delivery_time')}
                  data-testid="expected-delivery-time"
                />
              </Field>
              </fieldset>

              <Field label="Ramp In Date" hint="DD-MM-YYYY">
                <DateInput value={form.ramp_in_date} onChange={(v) => setForm((f) => ({ ...f, ramp_in_date: v }))} testId="ramp-in-date" />
              </Field>
              <Field label="Ramp In Time" hint="HH:00">
                <TextInput type="time" value={form.ramp_in_time} onChange={setField('ramp_in_time')} data-testid="ramp-in-time" />
              </Field>
              <Field label="Ramp Out Date" hint="DD-MM-YYYY">
                <DateInput value={form.ramp_out_date} onChange={(v) => setForm((f) => ({ ...f, ramp_out_date: v }))} testId="ramp-out-date" />
              </Field>

              <Field label="Ramp Out Time" hint="HH:00">
                <TextInput type="time" value={form.ramp_out_time} onChange={setField('ramp_out_time')} data-testid="ramp-out-time" />
              </Field>
              <Field label="Job Hours" hint="Calculated: Ramp Out − Ramp In">
                <input
                  value={calcJobHours(form.ramp_in_date, form.ramp_in_time, form.ramp_out_date, form.ramp_out_time)}
                  disabled
                  className={`${inputCx} bg-slate-50 text-slate-700 font-medium`}
                  data-testid="job-hours"
                />
              </Field>
              <Field label="Days Delay" hint="Calculated: Vehicle In − Ramp In">
                <input
                  value={calcDaysDelay(form.vehicle_in_date, form.vehicle_in_time, form.ramp_in_date, form.ramp_in_time)}
                  disabled
                  className={`${inputCx} bg-slate-50 text-slate-700 font-medium`}
                  data-testid="days-delay"
                />
              </Field>
            </div>
          )}

          {tab === 'customer' && (
            <fieldset disabled={isTechnician} className="contents">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2 flex items-center gap-3 p-4 rounded-xl bg-indigo-50/60 border border-indigo-100">
                <div className="text-[12.5px] text-indigo-900/80">
                  <span className="font-medium">Tip:</span> Click <em>Fetch from Sales</em> on the Vehicle Details tab — it now matches by Chassis No, Registration No, or Mobile No to auto-fill Customer + Address + Insurance + DMS details.
                </div>
              </div>
              <Field label="Party Name" required>
                <TextInput value={form.party_name} onChange={setField('party_name')} placeholder="Customer name" data-testid="party-name" />
              </Field>
              <Field label="Booking No">
                <TextInput value={form.booking_no} onChange={setField('booking_no')} placeholder="From Sales (auto-fetched)" data-testid="booking-no" />
              </Field>
              <Field label="Mobile No 1">
                <TextInput value={form.mobile_no_1} onChange={setField('mobile_no_1')} placeholder="10-digit mobile" data-testid="mobile-1" />
              </Field>
              <Field label="Mobile No 2">
                <TextInput value={form.mobile_no_2} onChange={setField('mobile_no_2')} placeholder="Alternate mobile" data-testid="mobile-2" />
              </Field>
              <Field label="Email" className="md:col-span-2">
                <TextInput type="email" value={form.email} onChange={setField('email')} placeholder="customer@example.com" data-testid="customer-email" />
              </Field>

              {/* iter-121: Additional Customer Details — collapsible section,
                  grouped by Address / Insurance / DMS. Auto-fetched along with
                  Customer & Vehicle when the user clicks "Fetch from Sales"
                  on the Vehicle Details tab. Existing layout untouched. */}
              <details className="md:col-span-2 mt-1 rounded-xl bg-white/60 border border-slate-200/70 overflow-hidden group" data-testid="additional-details-section">
                <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                  <span className="flex items-center gap-2">
                    <span className="inline-flex w-1.5 h-5 rounded-full bg-gradient-to-b from-indigo-400 to-fuchsia-400"></span>
                    Additional Details
                    <span className="text-[11px] font-normal text-slate-400 ml-1">Address · Insurance · DMS</span>
                  </span>
                  <span className="text-slate-400 text-[11px] group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-5 border-t border-slate-200/60">
                  {/* Address sub-group */}
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2.5">Address</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Building Name">
                        <TextInput value={form.building_name} onChange={setField('building_name')} placeholder="Optional" data-testid="building-name" />
                      </Field>
                      <Field label="Door No.">
                        <TextInput value={form.door_no} onChange={setField('door_no')} placeholder="e.g. 12-A" data-testid="door-no" />
                      </Field>
                      <Field label="Address Line 1" className="md:col-span-2">
                        <TextInput value={form.address_1} onChange={setField('address_1')} placeholder="Street / locality" data-testid="address-1" />
                      </Field>
                      <Field label="Address Line 2" className="md:col-span-2">
                        <TextInput value={form.address_2} onChange={setField('address_2')} placeholder="Optional" data-testid="address-2" />
                      </Field>
                      <Field label="Address Line 3" className="md:col-span-2">
                        <TextInput value={form.address_3} onChange={setField('address_3')} placeholder="Optional" data-testid="address-3" />
                      </Field>
                      <Field label="Area">
                        <TextInput value={form.area} onChange={setField('area')} placeholder="Area / neighbourhood" data-testid="area" />
                      </Field>
                      <Field label="City">
                        <TextInput value={form.city} onChange={setField('city')} placeholder="City" data-testid="city" />
                      </Field>
                      <Field label="State">
                        <TextInput value={form.state} onChange={setField('state')} placeholder="State" data-testid="state" />
                      </Field>
                      <Field label="Pincode">
                        <TextInput value={form.pin_code} onChange={setField('pin_code')} placeholder="6-digit PIN" data-testid="pin-code" />
                      </Field>
                    </div>
                  </div>

                  {/* Insurance sub-group */}
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2.5">Insurance</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Insurance Company">
                        <TextInput value={form.insurance_company_name} onChange={setField('insurance_company_name')} placeholder="e.g. ICICI Lombard" data-testid="insurance-company-name" />
                      </Field>
                      <Field label="Policy No.">
                        <TextInput value={form.policy_no} onChange={setField('policy_no')} placeholder="Policy number" data-testid="policy-no" />
                      </Field>
                      <Field label="Own Damage Expiry Date" hint="DD-MM-YYYY">
                        <DateInput value={form.own_damage_expiry_date} onChange={(v) => setForm((f) => ({ ...f, own_damage_expiry_date: v }))} testId="own-damage-expiry-date" />
                      </Field>
                    </div>
                  </div>

                  {/* DMS sub-group */}
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2.5">DMS</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="DMS Invoice No.">
                        <TextInput value={form.dms_invoice_no} onChange={setField('dms_invoice_no')} placeholder="Invoice number from DMS" data-testid="dms-invoice-no" />
                      </Field>
                      <Field label="DMS Invoice Date" hint="DD-MM-YYYY">
                        <DateInput value={form.dms_invoice_date} onChange={(v) => setForm((f) => ({ ...f, dms_invoice_date: v }))} testId="dms-invoice-date" />
                      </Field>
                    </div>
                  </div>
                </div>
              </details>
            </div>
            </fieldset>
          )}

          {tab === 'vehicle' && (
            <fieldset disabled={isTechnician} className="contents">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field label="Chassis No" required hint="Auto-fetch matches by Chassis / Reg No / Mobile" className="md:col-span-2">
                <div className="flex gap-2">
                  <TextInput value={form.chassis_no} onChange={setField('chassis_no')} placeholder="e.g. MA3ERLF1S00123456" data-testid="chassis-no" />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={fetchFromSales}
                      disabled={fetchingChassis}
                      data-testid="fetch-from-sales-btn"
                      className="shrink-0 px-3.5 py-2.5 rounded-xl bg-white/80 border border-indigo-200 text-indigo-700 text-[13px] font-medium hover:bg-indigo-50 transition-all flex items-center gap-2 disabled:opacity-60"
                    >
                      {fetchingChassis ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                      Fetch from Sales
                    </button>
                  )}
                </div>
              </Field>
              <Field label="Reg No">
                <TextInput value={form.reg_no} onChange={setField('reg_no')} placeholder="e.g. MH 02 AB 1234" data-testid="reg-no" />
              </Field>
              <Field label="Brand"><TextInput value={form.brand_name} onChange={setField('brand_name')} data-testid="brand" /></Field>
              <Field label="Model"><TextInput value={form.model} onChange={setField('model')} data-testid="model" /></Field>
              <Field label="Variant"><TextInput value={form.variant} onChange={setField('variant')} data-testid="variant" /></Field>
              <Field label="Color"><TextInput value={form.color} onChange={setField('color')} data-testid="color" /></Field>
              <Field label="Engine No"><TextInput value={form.engine_no} onChange={setField('engine_no')} data-testid="engine-no" /></Field>
              <Field label="KMS"><TextInput value={form.kms} onChange={setField('kms')} placeholder="Odometer" data-testid="kms" /></Field>
              <Field label="Battery No"><TextInput value={form.battery_no} onChange={setField('battery_no')} data-testid="battery-no" /></Field>
              <Field label="Charger No"><TextInput value={form.charger_no} onChange={setField('charger_no')} data-testid="charger-no" /></Field>
              <Field label="Controller No"><TextInput value={form.controller_no} onChange={setField('controller_no')} data-testid="controller-no" /></Field>
            </div>
            </fieldset>
          )}
        </fieldset>
      </div>

      <NotifyCustomerModal
        open={notifyState.open}
        jobId={notifyState.jobId}
        newStatus={notifyState.status}
        customer={notifyState.customer || {}}
        onClose={() => {
          setNotifyState((s) => ({ ...s, open: false }));
          handleBack();
        }}
      />

      {/* Delivered auto-fill confirmation */}
      {deliveredPrompt.open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
          onClick={cancelDeliveredAutofill}
          data-testid="delivered-autofill-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="delivered-autofill-modal"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-[16px] font-semibold text-slate-800 leading-tight">Auto-fill Delivery date & time?</h3>
                <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
                  Status changed to <span className="font-semibold text-slate-800">Delivered</span>. Set
                  <span className="font-medium text-slate-800"> Expected Delivery Date</span> to today and
                  <span className="font-medium text-slate-800"> Expected Delivery Time</span> to the current time?
                </p>
                <p className="text-[12px] text-slate-500 mt-1">You can still edit both fields afterwards.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={cancelDeliveredAutofill}
                data-testid="delivered-autofill-cancel"
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeliveredAutofill}
                data-testid="delivered-autofill-confirm"
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-emerald-600 hover:bg-emerald-700"
              >
                Yes, auto-fill
              </button>
            </div>
          </div>
        </div>
      )}
    </ServiceLayout>
  );
}
